/**
 * AcceptPlatformInvitationConsumePage — token-consume side of the
 * PUBLIC `/accept-platform-invitation` flow (SCRUM-131).
 *
 * This is the platform-staff sibling of the org
 * `AcceptInvitationConsumePage`. It COEXISTS with that flow but hits
 * DIFFERENT endpoints (`/auth/platform-invitations/{preview,accept}`),
 * consumes DIFFERENT schemas, and ends DIFFERENTLY: the accept call
 * does NOT mint a session, so the success CTA routes to `/login`,
 * never to the dashboard.
 *
 * Lifecycle ("Refined A" — optimistic form):
 *
 *  1. On mount, POST /auth/platform-invitations/preview to learn the
 *     invited `email`, `platformRole` ('root'|'support'), and
 *     `expiresAt`. An empty token, or ANY non-ok preview (the BE is
 *     anti-enum and returns a byte-identical 401 for every token-invalid
 *     cause, plus 429 rate-limit), collapses to ONE invalid-link
 *     surface — the FE never amplifies the leak. Unlike the org flow,
 *     preview does NOT return `requires_new_account`, so we cannot
 *     pre-branch new-vs-existing; the form is shown optimistically.
 *
 *  2. The form shows First name, Last name, and Password — all
 *     OPTIONAL at the form level (password is only structurally
 *     required when the BE later says so). A non-blocking hint pre-empts
 *     the existing-user case.
 *
 *  3. On submit, POST /auth/platform-invitations/accept and map the
 *     response shapes to dedicated surfaces:
 *       - 200 → success ("account ready, sign in"; 2FA at sign-in) → /login
 *       - 401 invitation_login_required → login-required surface
 *       - 403 invitation_email_mismatch → wrong-account surface
 *       - 422 invitation_password_required → mark password required, stay
 *       - 401 invitation_token_invalid (or any other 401) → invalid-link
 *       - 429 / 5xx / network → temporary-failure copy
 *
 * Tokens-only colors via @sparta/design-tokens; touch targets ≥ 44 px;
 * `role="status"` aria-live regions; semantic landmarks; labelled
 * inputs per CLAUDE.md Frontend rule §7 (WCAG 2.1 AA).
 */

import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  acceptPlatformInvitationBodySchema,
  type PreviewPlatformInvitationResponse,
} from '@artemis/validators';
import { Button, Card, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

// ---------------------------------------------------------------------------
// Form schema — derived from the canonical platform wire contract.
//
// password is OPTIONAL at the form level (Refined A): a returning
// platform user does not set one. firstName/lastName are also optional.
// When supplied, password keeps the wire min-length (12) so the BE
// 422 is reserved for the genuine "new account, no password" case.
// ---------------------------------------------------------------------------

// The wire contract is the single source of truth (codex MEDIUM-5):
// the password ceiling (128) is lifted from `acceptPlatformInvitationBodySchema`
// so a max-length drift fails here too. Empty text inputs render as `''`,
// not `undefined`; the wire schema's `.min(...)` on the name + password
// fields would reject a blank-but-optional field and silently block submit,
// so the form-level schema treats empty strings as "omitted" (they are
// stripped on submit) while keeping the wire password floor (12) when present.
const PASSWORD_MAX_LENGTH = (() => {
  const shape = acceptPlatformInvitationBodySchema.shape.password;
  // Unwrap the `.optional()` to read the inner string checks.
  const inner = shape instanceof z.ZodOptional ? shape.unwrap() : shape;
  const maxCheck =
    inner instanceof z.ZodString
      ? inner._def.checks.find((c) => c.kind === 'max')
      : undefined;
  return maxCheck !== undefined && maxCheck.kind === 'max'
    ? maxCheck.value
    : 128;
})();

const platformFormSchema = z.object({
  firstName: z.string().max(100),
  lastName: z.string().max(100),
  password: z
    .string()
    .max(PASSWORD_MAX_LENGTH)
    .refine((value) => value.length === 0 || value.length >= 12, {
      message: 'Password must be at least 12 characters.',
    }),
});

type PlatformFormInput = z.infer<typeof platformFormSchema>;

// ---------------------------------------------------------------------------
// Page state machine
// ---------------------------------------------------------------------------

type PageState =
  | { kind: 'loading-preview' }
  | { kind: 'invalid-link' }
  | {
      kind: 'form';
      preview: PreviewPlatformInvitationResponse;
      submit: SubmitState;
      passwordRequiredMessage?: string;
    }
  | { kind: 'login-required'; email: string }
  | { kind: 'email-mismatch'; emailMasked: string }
  | { kind: 'success' };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'temporary-failure'; message: string };

// ---------------------------------------------------------------------------
// Helpers for safely peeking into JSON-typed error bodies
// ---------------------------------------------------------------------------

function readErrorCode(maybeError: unknown): string | undefined {
  if (typeof maybeError !== 'object' || maybeError === null) return undefined;
  const value = (maybeError as { error?: unknown }).error;
  return typeof value === 'string' ? value : undefined;
}

function readEmailMasked(maybeBody: unknown): string {
  if (typeof maybeBody !== 'object' || maybeBody === null) return '';
  const value = (maybeBody as { email_masked?: unknown }).email_masked;
  return typeof value === 'string' ? value : '';
}

function roleLabel(role: PreviewPlatformInvitationResponse['platformRole']): string {
  return role === 'root' ? 'Root' : 'Support';
}

function formatExpiry(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface AcceptPlatformInvitationConsumePageProps {
  /**
   * Raw invitation token from the `?token=` query param. The page
   * short-circuits to the invalid-link surface if the token is empty;
   * otherwise the BE preview endpoint validates it via constant-time
   * Argon2id verify.
   */
  token: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AcceptPlatformInvitationConsumePage({
  token,
}: AcceptPlatformInvitationConsumePageProps): React.ReactElement {
  const [state, setState] = React.useState<PageState>(() =>
    token.length > 0 ? { kind: 'loading-preview' } : { kind: 'invalid-link' },
  );

  // The form hook is created unconditionally (React rules); the form is
  // rendered only when the state branch demands it.
  const form = useForm<PlatformFormInput>({
    resolver: zodResolver(platformFormSchema),
    mode: 'onTouched',
    defaultValues: { firstName: '', lastName: '', password: '' },
  });

  // ------------------------------------------------------------ preview call
  React.useEffect(() => {
    if (token.length === 0) {
      return;
    }
    let cancelled = false;

    void (async (): Promise<void> => {
      try {
        const result = await apiClient.POST(
          '/auth/platform-invitations/preview',
          { body: { token } },
        );
        if (cancelled) return;
        const { response } = result;
        if (!response.ok) {
          // ALL preview failures (anti-enum 401, 429, anything) collapse
          // to the single invalid-link surface.
          setState({ kind: 'invalid-link' });
          return;
        }
        const data = (result as { data?: unknown }).data;
        // Soft-validate the shape: if the BE drifts, treat as invalid so
        // the FE never renders an undefined email/role. apiClient already
        // reports drift to Sentry.
        if (
          typeof data !== 'object' ||
          data === null ||
          typeof (data as { email?: unknown }).email !== 'string' ||
          ((data as { platformRole?: unknown }).platformRole !== 'root' &&
            (data as { platformRole?: unknown }).platformRole !== 'support') ||
          typeof (data as { expiresAt?: unknown }).expiresAt !== 'string'
        ) {
          setState({ kind: 'invalid-link' });
          return;
        }
        const preview = data as PreviewPlatformInvitationResponse;
        setState({ kind: 'form', preview, submit: { kind: 'idle' } });
      } catch {
        if (cancelled) return;
        // Network/unhandled error during preview — fall back to the
        // invalid-link surface; the recovery action is the same.
        setState({ kind: 'invalid-link' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // ----------------------------------------------------------- accept caller
  const performAccept = React.useCallback(
    async (currentState: PageState, values: PlatformFormInput): Promise<void> => {
      if (currentState.kind !== 'form') {
        return;
      }
      const preview = currentState.preview;

      setState({
        kind: 'form',
        preview,
        submit: { kind: 'submitting' },
      });

      try {
        const body: {
          token: string;
          firstName?: string;
          lastName?: string;
          password?: string;
        } = { token };
        if (values.firstName.length > 0) {
          body.firstName = values.firstName;
        }
        if (values.lastName.length > 0) {
          body.lastName = values.lastName;
        }
        if (values.password.length > 0) {
          body.password = values.password;
        }

        const result = await apiClient.POST(
          '/auth/platform-invitations/accept',
          { body },
        );
        const { response } = result;

        if (response.ok) {
          // No session is minted — the user must sign in next.
          setState({ kind: 'success' });
          return;
        }

        const errorBody = (result as { error?: unknown }).error;
        const code = readErrorCode(errorBody);

        if (response.status === 401 && code === 'invitation_login_required') {
          setState({ kind: 'login-required', email: preview.email });
          return;
        }
        if (response.status === 403 && code === 'invitation_email_mismatch') {
          setState({
            kind: 'email-mismatch',
            emailMasked: readEmailMasked(errorBody),
          });
          return;
        }
        if (
          response.status === 422 &&
          code === 'invitation_password_required'
        ) {
          // New account but password missing — keep the user on the form
          // and flag the password field as required.
          setState({
            kind: 'form',
            preview,
            submit: { kind: 'idle' },
            passwordRequiredMessage: 'Set a password to finish.',
          });
          return;
        }
        if (response.status === 401) {
          // Anti-enum 401 (invalid/expired/revoked/concurrent) — collapse.
          setState({ kind: 'invalid-link' });
          return;
        }
        if (response.status === 429) {
          setState({
            kind: 'form',
            preview,
            submit: {
              kind: 'temporary-failure',
              message:
                'Too many attempts. Please wait a moment and try again.',
            },
          });
          return;
        }
        if (response.status >= 500) {
          setState({
            kind: 'form',
            preview,
            submit: {
              kind: 'temporary-failure',
              message:
                'Acceptance is temporarily unavailable. Try again shortly.',
            },
          });
          return;
        }
        setState({
          kind: 'form',
          preview,
          submit: {
            kind: 'temporary-failure',
            message: 'Something went wrong. Try again.',
          },
        });
      } catch {
        setState({
          kind: 'form',
          preview,
          submit: {
            kind: 'temporary-failure',
            message: 'Network error. Check your connection and try again.',
          },
        });
      }
    },
    [token],
  );

  // ---------------------------------------------------------- form handler
  const onSubmit = form.handleSubmit(async (values) => {
    await performAccept(state, values);
  });

  // ------------------------------------------------------------- shell
  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        {state.kind === 'loading-preview' ? (
          <LoadingPreviewSurface />
        ) : state.kind === 'invalid-link' ? (
          <InvalidLinkSurface />
        ) : state.kind === 'login-required' ? (
          <LoginRequiredSurface email={state.email} />
        ) : state.kind === 'email-mismatch' ? (
          <EmailMismatchSurface emailMasked={state.emailMasked} />
        ) : state.kind === 'success' ? (
          <SuccessSurface />
        ) : (
          <FormSurface
            preview={state.preview}
            submit={state.submit}
            passwordRequiredMessage={state.passwordRequiredMessage}
            form={form}
            onSubmit={onSubmit}
          />
        )}
      </div>
    </main>
  );
}

// ===========================================================================
// Surfaces
// ===========================================================================

function LoadingPreviewSurface(): React.ReactElement {
  return (
    <section role="status" aria-live="polite">
      <p className="text-sm" style={{ color: 'var(--fg-tertiary)' }}>
        Checking invitation…
      </p>
    </section>
  );
}

function InvalidLinkSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1 className="text-3xl font-bold" style={{ letterSpacing: '-0.6px' }}>
          This invitation link is not valid
        </h1>
        <p className="mt-3 text-sm" style={{ color: 'var(--fg-tertiary)' }}>
          This invitation can no longer be used. Ask a platform
          administrator to send a new invitation.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          ← Back to sign in
        </a>
      </section>
    </Card>
  );
}

function LoginRequiredSurface(props: { email: string }): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1 className="text-3xl font-bold" style={{ letterSpacing: '-0.6px' }}>
          Sign in to accept this invitation
        </h1>
        <p className="mt-3 text-sm" style={{ color: 'var(--fg-tertiary)' }}>
          This invite is for an existing account ({props.email}). Sign in,
          then reopen this link from your email to finish accepting.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Sign in →
        </a>
      </section>
    </Card>
  );
}

function EmailMismatchSurface(props: {
  emailMasked: string;
}): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1 className="text-3xl font-bold" style={{ letterSpacing: '-0.6px' }}>
          You're signed in as the wrong account
        </h1>
        <p className="mt-3 text-sm" style={{ color: 'var(--fg-tertiary)' }}>
          This invitation is for {props.emailMasked}, but you're signed in as
          a different user. Sign out, sign in as {props.emailMasked}, then
          reopen the invitation link from your email.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Go to sign in →
        </a>
      </section>
    </Card>
  );
}

function SuccessSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1 className="text-3xl font-bold" style={{ letterSpacing: '-0.6px' }}>
          You're all set
        </h1>
        <p className="mt-3 text-sm" style={{ color: 'var(--fg-tertiary)' }}>
          Your platform account is ready. Sign in to continue — you'll set up
          two-factor authentication during sign-in.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Sign in →
        </a>
      </section>
    </Card>
  );
}

function FormSurface(props: {
  preview: PreviewPlatformInvitationResponse;
  submit: SubmitState;
  passwordRequiredMessage?: string;
  form: ReturnType<typeof useForm<PlatformFormInput>>;
  onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
}): React.ReactElement {
  const { preview, submit, passwordRequiredMessage, form, onSubmit } = props;
  const submitting = submit.kind === 'submitting';
  const errors = form.formState.errors;

  const submitErrorMessage: string | undefined =
    submit.kind === 'temporary-failure' ? submit.message : undefined;

  return (
    <>
      <PlatformInvitationHeader preview={preview} />

      <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
        <TextField
          label="First name"
          autoComplete="given-name"
          disabled={submitting}
          error={errors.firstName?.message}
          {...form.register('firstName')}
        />

        <TextField
          label="Last name"
          autoComplete="family-name"
          disabled={submitting}
          error={errors.lastName?.message}
          {...form.register('lastName')}
        />

        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          disabled={submitting}
          hint="If you already have an account, you'll be asked to sign in instead."
          error={errors.password?.message ?? passwordRequiredMessage}
          {...form.register('password')}
        />

        <div role="alert" aria-live="polite" className="mb-3 min-h-5">
          {submitErrorMessage !== undefined ? (
            <p className="text-sm" style={{ color: 'var(--fg-danger)' }}>
              {submitErrorMessage}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          disabled={submitting}
          aria-busy={submitting}
          aria-disabled={submitting}
        >
          {submitting ? 'Accepting…' : 'Accept invitation'}
        </Button>
      </form>
    </>
  );
}

function PlatformInvitationHeader(props: {
  preview: PreviewPlatformInvitationResponse;
}): React.ReactElement {
  const { preview } = props;
  return (
    <>
      <h1 className="text-3xl font-bold" style={{ letterSpacing: '-0.6px' }}>
        Accept your platform invitation
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-tertiary)' }}>
        Invited as <strong>{preview.email}</strong>.
      </p>
      <p className="mb-8 mt-2 flex items-center gap-2 text-sm">
        <span
          className="inline-flex min-h-touch items-center rounded-full px-3 text-[13px] font-semibold"
          style={{
            background: 'var(--bg-subtle)',
            color: 'var(--fg-primary)',
          }}
        >
          {roleLabel(preview.platformRole)}
        </span>
        <span style={{ color: 'var(--fg-tertiary)' }}>
          expires {formatExpiry(preview.expiresAt)}
        </span>
      </p>
    </>
  );
}
